import { useCollections } from "api/collections";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { COLLECTIONS_LIST_ID } from "state/tables/types";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { formatNumberValue } from "utilities/numbers";

export const CollectionsList = () => {
  const { message } = useMessage();
  const navigate = useNavigate();
  const { setColumnOrdering } = useTablesStateHelpers();
  const { listing, updateListing } = useListing(COLLECTIONS_LIST_ID);

  const { useCollectionsList } = useCollections();
  const { data: collectionsList } = useCollectionsList({
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
  });

  const headItems = useMemo(
    () =>
      createHeadItems([
        {
          headerText: message({ id: "collectionName" }),
          property: "name",
          isSortable: true,
        },
        {
          headerText: message({ id: "status" }),
          property: "status",
          isSortable: false,
        },
        {
          headerText: message({ id: "recordsInCollection" }),
          property: "recordsInGroup",
          isSortable: false,
          align: "right",
        },
        {
          headerText: message({ id: "updatedOn" }),
          property: "updatedAt",
          isSortable: true,
        },
      ]),
    [message],
  );

  const itemsList = useMemo(
    () =>
      collectionsList?.items.map((item) => ({
        cells: [
          {
            content: item.name,
            property: "name",
          },
          {
            content: <StatusValue status={item.active} />,
            property: "status",
          },
          {
            content: formatNumberValue(item.recordsInGroup),
            property: "recordsInGroup",
            align: "right",
          },
          { content: OLDate(item.updatedAt), property: "updatedOn" },
        ],
        id: item.groupOfValuesId,
        metadata: {
          disabled: !item.active,
        },
        testId: "groups-list-row",
      })),
    [collectionsList?.items],
  );

  const items = collectionsList?.items ? (itemsList as Row[]) : [];

  return (
    <>
      <TopHeader title={message({ id: "collections" })} />
      <Subheader>
        <SubheaderWrapper alignRight>
          <SubheaderLinkButton
            text={message({ id: "addCollection" })}
            link={links.addCollection}
            data-test-id="list-of-groups-add-group-add-new-collection-button"
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={COLLECTIONS_LIST_ID}
        data-test-id="groups-of-values"
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editCollection(id as string)),
              label: message({ id: "edit" }),
            },
          ],
        }}
        columns={collectionsList && headItems}
        onRowClick={(id: string) => {
          navigate(links.collection(id as string));
        }}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(
            collectionsList?.total.filtered,
            collectionsList?.total.all,
          ),
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={collectionsList && items}
        sort={{
          order: listing.order || "asc",
          property: listing.property || "name",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
      />
    </>
  );
};
